type Rect = { left: number; top: number; width: number; height: number };
type Ink = { left: number; top: number; right: number; bottom: number };
const overlaps = (a: Rect, b: Rect) => a.left < b.left + b.width && a.left + a.width > b.left
  && a.top < b.top + b.height && a.top + a.height > b.top;

/** Scale around the same bottom-center origin used by the recording player. */
export function hostMediaScale(host: Rect, ink: Ink, media: Rect, minimum = .42): number {
  const occupied = (scale: number): Rect => ({
    left: host.left + host.width / 2 + (ink.left - .5) * host.width * scale,
    top: host.top + host.height + (ink.top - 1) * host.height * scale,
    width: (ink.right - ink.left) * host.width * scale,
    height: (ink.bottom - ink.top) * host.height * scale,
  });
  if (!overlaps(occupied(1), media)) return 1;
  for (let step = 99; step >= Math.round(minimum * 100); step--)
    if (!overlaps(occupied(step / 100), media)) return step / 100;
  return minimum;
}

const silhouettes = new WeakMap<HTMLCanvasElement, Map<string, Ink>>();
function silhouette(canvas: HTMLCanvasElement): Ink {
  const key = canvas.dataset.characterFrame;
  let frames = silhouettes.get(canvas);
  const cached = key ? frames?.get(key) : undefined; if (cached) return cached;
  const { width, height } = canvas;
  const fallback = { left: 0, top: 0, right: 1, bottom: 1 };
  if (!width || !height) return fallback;
  const pixels = canvas.getContext('2d')?.getImageData(0, 0, width, height).data;
  if (!pixels) return fallback;
  let left = width, top = height, right = 0, bottom = 0;
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) if (pixels[(y * width + x) * 4 + 3] > 16) {
    left = Math.min(left, x); right = Math.max(right, x + 1); top = Math.min(top, y); bottom = Math.max(bottom, y + 1);
  }
  if (right <= left) return fallback; // The renderer may still be loading.
  const result = { left: left / width, top: top / height, right: right / width, bottom: bottom / height };
  if (key) {
    if (!frames) { frames = new Map(); silhouettes.set(canvas, frames); }
    if (frames.size >= 256) frames.clear();
    frames.set(key, result);
  }
  return result;
}

export function clearHostMediaScale(scene: Element) {
  scene.removeAttribute('data-recording-media-hides-brand');
  for (const canvas of scene.querySelectorAll<HTMLCanvasElement>('.actor canvas')) canvas.style.removeProperty('--edition-host-scale');
}

function fullHost(canvas: HTMLCanvasElement): Rect {
  const rect = canvas.getBoundingClientRect(), scale = Number(canvas.style.getPropertyValue('--edition-host-scale')) || 1;
  return { left: rect.left - (rect.width / scale - rect.width) / 2, top: rect.bottom - rect.height / scale,
    width: rect.width / scale, height: rect.height / scale };
}

export function recordingMediaSpace(frame: Rect, hostTops: number[], brandBottom?: number, aspect?: number): Rect {
  const gutter = Math.max(4, frame.width * .008);
  // Short landscape stages need enough picture area to remain useful. Only
  // hosts obstructing this area are subsequently reduced by the overlap test.
  const minimumBottom = frame.top + (frame.width > frame.height ? frame.height * .45 : 0);
  const bottom = Math.min(frame.top + frame.height - gutter, Math.max(minimumBottom, Math.min(...hostTops.map(y => y - gutter))));
  // A compact landscape frame may have no usable space between logo and heads.
  // Let the picture use the upper region, as full-stage media already does.
  const useBrand = brandBottom !== undefined && bottom - brandBottom - gutter >= Math.max(32, frame.height * .25);
  const top = Math.max(frame.top + gutter, (useBrand ? brandBottom! : frame.top) + gutter);
  const width = Math.max(1, frame.width - 2 * gutter), availableHeight = Math.max(1, bottom - top);
  const height = aspect && aspect > 0 ? Math.min(availableHeight, width / aspect) : availableHeight;
  // Center the contained picture in the upper region instead of pinning a
  // landscape image against the logo and leaving all spare room below it.
  return { left: frame.left + gutter, top: top + (availableHeight - height) / 2, width, height };
}

/** Measure the contained picture, excluding letterboxing, against full-size host ink.
 * Undo the last scale for measurement so an overlapping host cannot oscillate. */
export function updateHostMediaScale(figure: HTMLElement, aspect: number) {
  const scene = figure.closest('.studio-scene'), picture = figure.querySelector('.edition-visual-picture');
  if (!scene || !picture || !(aspect > 0)) return;
  const hosts = [...scene.querySelectorAll<HTMLCanvasElement>('.actor canvas')];
  if (figure.closest('.record-mode')) {
    const shell = figure.closest<HTMLElement>('.stage-shell')!;
    const rect = shell.getBoundingClientRect();
    const frame = { left: rect.left + shell.clientLeft, top: rect.top + shell.clientTop, width: shell.clientWidth, height: shell.clientHeight };
    const brand = scene.querySelector<HTMLElement>('.stage-brand');
    const brandBottom = brand && !figure.classList.contains('edition-visual--cutaway') ? brand.getBoundingClientRect().bottom : undefined;
    const area = recordingMediaSpace(frame, hosts.map(canvas => { const full = fullHost(canvas); return full.top + silhouette(canvas).top * full.height; }), brandBottom, aspect);
    scene.toggleAttribute('data-recording-media-hides-brand', brandBottom !== undefined && area.top < brandBottom);
    const target = figure.parentElement?.classList.contains('recording-media-position') ? figure.parentElement : figure;
    target.style.left = `${area.left - frame.left}px`; target.style.top = `${area.top - frame.top}px`;
    target.style.width = `${area.width}px`; target.style.height = `${area.height}px`;
    target.style.right = 'auto'; target.style.bottom = 'auto';
  }
  const box = picture.getBoundingClientRect(), width = Math.min(box.width, box.height * aspect);
  const media = { left: box.left + (box.width - width) / 2, top: box.top, width, height: width / aspect };
  figure.style.setProperty('--edition-media-credit-width', `${width}px`);
  figure.toggleAttribute('data-compact-credit', Boolean(figure.closest('.record-mode')) && width < 180);
  for (const canvas of hosts) {
    canvas.style.setProperty('--edition-host-scale', String(hostMediaScale(fullHost(canvas), silhouette(canvas), media)));
  }
}
